import React, { useEffect } from 'react';
import { FileText, CreditCard, Shield, Phone, Scale } from 'lucide-react';
import SharedNavigation from './SharedNavigation';
import './SharedStyles.css';

const Terms: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="app-page">
      <SharedNavigation />

      <main className="app-main">
        <div className="app-container-narrow">
          <div className="text-center mb-6">
            <h1 className="app-title">Allgemeine Geschäftsbedingungen</h1>
            <p className="app-subtitle">
              Transparente und faire Bedingungen für die Nutzung der Pfändungstabelle API
            </p>
          </div>

          {/* Quick Overview */}
          <div className="app-content" style={{ marginBottom: '2rem' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FileText size={24} style={{ color: 'var(--voidmind-primary)' }} />
              Wichtige Punkte im Überblick
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
              <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <CreditCard size={20} style={{ color: 'var(--voidmind-primary)' }} />
                  <strong>Monatliche Abrechnung</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Flexible Kündigung jederzeit zum Monatsende möglich</p>
              </div>
              <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Shield size={20} style={{ color: 'var(--success-green)' }} />
                  <strong>99.9% Uptime</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Garantierte Verfügbarkeit mit SLA für Enterprise</p>
              </div>
              <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Phone size={20} style={{ color: 'var(--voidmind-primary)' }} />
                  <strong>Support inklusive</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>E-Mail-Support für alle, Telefon ab Business-Plan</p>
              </div>
            </div>
          </div>
          
          <div className="app-content">
            <section>
              <h2>§ 1 Geltungsbereich</h2>
              <p>
                Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge zwischen 
                VoidMind und dem Kunden über die Nutzung der Pfändungstabelle API.
              </p>
            </section>

            <section>
              <h2>§ 2 Vertragsgegenstand</h2>
              <p>
                Gegenstand des Vertrages ist die Bereitstellung einer API zum Abruf aktueller 
                deutscher Pfändungstabellen in maschinenlesbarem Format (JSON).
              </p>
              <h3>Leistungsumfang:</h3>
              <ul>
                <li>REST-API für Pfändungstabellen-Daten</li>
                <li>Aktuelle und historische Daten je nach Tarif</li>
                <li>Technischer Support per E-Mail</li>
                <li>99.9% Verfügbarkeitsgarantie</li>
              </ul>
            </section>

            <section>
              <h2>§ 3 Nutzungsrechte</h2>
              <p>
                Der Kunde erhält ein nicht-exklusives, nicht übertragbares Recht zur Nutzung 
                der API entsprechend dem gewählten Tarif. Die Nutzung ist auf die vereinbarte 
                Anzahl von API-Aufrufen pro Monat begrenzt.
              </p>
              <h3>Erlaubte Nutzung:</h3>
              <ul>
                <li>Integration in eigene Software-Anwendungen</li>
                <li>Verwendung für geschäftliche Zwecke</li>
                <li>Caching der Daten für bis zu 24 Stunden</li>
              </ul>
              <h3>Verbotene Nutzung:</h3>
              <ul>
                <li>Weiterverkauf oder Weitergabe der API-Zugangsdaten</li>
                <li>Reverse Engineering der API</li>
                <li>Überschreitung der vereinbarten Nutzungslimits</li>
                <li>Verwendung für illegale Zwecke</li>
              </ul>
            </section>

            <section>
              <h2>§ 4 Preise und Zahlung</h2>
              <p>
                Die Preise richten sich nach der aktuellen Preisliste auf der Website. 
                Alle Preise verstehen sich inklusive der gesetzlichen Mehrwertsteuer.
              </p>
              <h3>Zahlungsbedingungen:</h3>
              <ul>
                <li>Monatliche Abrechnung im Voraus</li>
                <li>Zahlung per Kreditkarte über Stripe</li>
                <li>Automatische Verlängerung bei aktiven Abonnements</li>
                <li>Kündigung jederzeit zum Monatsende möglich</li>
              </ul>
            </section>

            <section>
              <h2>§ 5 Verfügbarkeit und Support</h2>
              <p>
                Wir garantieren eine Verfügbarkeit der API von 99.9% pro Monat. 
                Wartungsarbeiten werden rechtzeitig angekündigt.
              </p>
              <h3>Support-Leistungen:</h3>
              <ul>
                <li>E-Mail-Support für alle Tarife</li>
                <li>Telefon-Support für Business+ Tarife</li>
                <li>Dedicated Account Manager für Enterprise</li>
                <li>Technische Dokumentation und Code-Beispiele</li>
              </ul>
            </section>

            <section>
              <h2>§ 6 Haftung</h2>
              <p>
                Die Haftung für Schäden ist auf Vorsatz und grobe Fahrlässigkeit beschränkt. 
                Für die Richtigkeit und Vollständigkeit der bereitgestellten Daten übernehmen 
                wir keine Gewähr, da diese auf offiziellen Quellen basieren.
              </p>
            </section>

            <section>
              <h2>§ 7 Kündigung</h2>
              <p>
                Der Vertrag kann von beiden Seiten jederzeit zum Ende des laufenden 
                Abrechnungszeitraums gekündigt werden. Bei Verstößen gegen diese AGB 
                behalten wir uns eine außerordentliche Kündigung vor.
              </p>
            </section>

            <section>
              <h2>§ 8 Schlussbestimmungen</h2>
              <p>
                Es gilt deutsches Recht. Gerichtsstand ist München. 
                Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit 
                der übrigen Bestimmungen unberührt.
              </p>
              <p style={{ marginTop: '2rem', fontStyle: 'italic', color: 'var(--text-light)' }}>
                Stand: Januar 2025
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
